import type { SectorData } from './summary.types.ts';

const SP500_SECTOR_ALLOCATIONS = {
  'Communication Services': 9.3,
  'Consumer Discretionary': 10.2,
  'Consumer Staples': 5.6,
  Energy: 3.2,
  Financials: 13.8,
  'Health Care': 10.1,
  Industrials: 8.2,
  'Information Technology': 32.6,
  Materials: 2.1,
  'Real Estate': 2.0,
  Utilities: 2.9,
};

const US_TOTAL_MARKET_SECTOR_ALLOCATIONS = {
  'Communication Services': 8.9,
  'Consumer Discretionary': 10.3,
  'Consumer Staples': 5.5,
  Energy: 3.5,
  Financials: 14.0,
  'Health Care': 10.9,
  Industrials: 8.8,
  'Information Technology': 30.0,
  Materials: 2.4,
  'Real Estate': 2.7,
  Utilities: 3.0,
};

const NASDAQ_100_SECTOR_ALLOCATIONS = {
  'Communication Services': 14.9,
  'Consumer Discretionary': 13.8,
  'Consumer Staples': 5.8,
  Energy: 0.5,
  Financials: 0.9,
  'Health Care': 5.6,
  Industrials: 4.8,
  'Information Technology': 51.0,
  Materials: 0.6,
  'Real Estate': 0.2,
  Utilities: 1.9,
};

const DOW_30_SECTOR_ALLOCATIONS = {
  'Communication Services': 4.0,
  'Consumer Discretionary': 13.0,
  'Consumer Staples': 8.0,
  Energy: 4.0,
  Financials: 16.0,
  'Health Care': 18.0,
  Industrials: 15.0,
  'Information Technology': 19.0,
  Materials: 2.0,
  'Real Estate': 0.0,
  Utilities: 1.0,
};

const RUSSELL_2000_SECTOR_ALLOCATIONS = {
  'Communication Services': 2.0,
  'Consumer Discretionary': 11.0,
  'Consumer Staples': 3.0,
  Energy: 6.0,
  Financials: 17.0,
  'Health Care': 16.0,
  Industrials: 18.0,
  'Information Technology': 14.0,
  Materials: 5.0,
  'Real Estate': 6.0,
  Utilities: 2.0,
};

const SP500_GROWTH_SECTOR_ALLOCATIONS = {
  'Communication Services': 13.0,
  'Consumer Discretionary': 12.0,
  'Consumer Staples': 3.0,
  Energy: 1.0,
  Financials: 7.0,
  'Health Care': 8.0,
  Industrials: 6.0,
  'Information Technology': 47.0,
  Materials: 1.0,
  'Real Estate': 1.0,
  Utilities: 1.0,
};

const DEVELOPED_EX_US_SECTOR_ALLOCATIONS = {
  'Communication Services': 4.5,
  'Consumer Discretionary': 10.5,
  'Consumer Staples': 9.0,
  Energy: 4.5,
  Financials: 22.0,
  'Health Care': 12.5,
  Industrials: 17.0,
  'Information Technology': 9.0,
  Materials: 7.0,
  'Real Estate': 2.0,
  Utilities: 2.0,
};

const EMERGING_MARKETS_SECTOR_ALLOCATIONS = {
  'Communication Services': 9.0,
  'Consumer Discretionary': 13.0,
  'Consumer Staples': 5.0,
  Energy: 5.0,
  Financials: 23.0,
  'Health Care': 4.0,
  Industrials: 7.0,
  'Information Technology': 22.0,
  Materials: 7.0,
  'Real Estate': 2.0,
  Utilities: 3.0,
};

const CLEAN_ENERGY_SECTOR_ALLOCATIONS = {
  Utilities: 55,
  Industrials: 30,
  'Information Technology': 15,
};

const SYMBOL_TO_SECTOR_DATA: Record<string, SectorData> = {
  SPX: indexData('SPX', 'S&P 500 Index', 'United States', SP500_SECTOR_ALLOCATIONS),
  SPXW: indexData('SPXW', 'S&P 500 Index', 'United States', SP500_SECTOR_ALLOCATIONS),
  SPY: indexData('SPY', 'SPDR S&P 500 ETF Trust', 'United States', SP500_SECTOR_ALLOCATIONS),
  VOO: indexData('VOO', 'Vanguard S&P 500 ETF', 'United States', SP500_SECTOR_ALLOCATIONS),
  VTI: indexData('VTI', 'US Total Market ETF', 'United States', US_TOTAL_MARKET_SECTOR_ALLOCATIONS),
  QQQ: indexData('QQQ', 'Invesco QQQ Trust', 'United States', NASDAQ_100_SECTOR_ALLOCATIONS),
  QQQM: indexData('QQQM', 'Invesco NASDAQ 100 ETF', 'United States', NASDAQ_100_SECTOR_ALLOCATIONS),
  DIA: indexData('DIA', 'Dow Jones Industrial Average ETF', 'United States', DOW_30_SECTOR_ALLOCATIONS),
  IWM: indexData('IWM', 'Russell 2000 ETF', 'United States', RUSSELL_2000_SECTOR_ALLOCATIONS),
  VOOG: indexData('VOOG', 'Vanguard S&P 500 Growth ETF', 'United States', SP500_GROWTH_SECTOR_ALLOCATIONS),
  EFA: indexData('EFA', 'iShares MSCI EAFE ETF', 'International Developed', DEVELOPED_EX_US_SECTOR_ALLOCATIONS),
  VEA: indexData('VEA', 'Vanguard FTSE Developed Markets ETF', 'International Developed', DEVELOPED_EX_US_SECTOR_ALLOCATIONS),
  EEM: indexData('EEM', 'iShares MSCI Emerging Markets ETF', 'Emerging Markets', EMERGING_MARKETS_SECTOR_ALLOCATIONS),
  VWO: indexData('VWO', 'Vanguard FTSE Emerging Markets ETF', 'Emerging Markets', EMERGING_MARKETS_SECTOR_ALLOCATIONS),
  ICLN: indexData('ICLN', 'iShares Global Clean Energy ETF', 'Global / Thematic', CLEAN_ENERGY_SECTOR_ALLOCATIONS),
  GDX: singleSectorData('GDX', 'VanEck Gold Miners ETF', 'Materials'),
  GLD: singleSectorData('GLD', 'Gold Shares ETF', 'Materials'),
  SLV: singleSectorData('SLV', 'Silver Shares ETF', 'Materials'),
  BOXX: singleSectorData('BOXX', 'Alpha Architect 1-3 Month Box ETF', 'Cash & equivalents'),
  TLT: singleSectorData('TLT', '20+ Year Treasury Bond ETF', 'Fixed Income'),
  IEF: singleSectorData('IEF', '7-10 Year Treasury Bond ETF', 'Fixed Income'),
  SHY: singleSectorData('SHY', '1-3 Year Treasury Bond ETF', 'Fixed Income'),
  BND: singleSectorData('BND', 'Total Bond Market ETF', 'Fixed Income'),
  BTC: singleSectorData('BTC', 'Bitcoin', 'Crypto / Alternatives', 'Global / Thematic'),
};

function indexData(
  symbol: string,
  industry: string,
  country = 'United States',
  sectorAllocations: Record<string, number>
): SectorData {
  return {
    symbol,
    sector: 'Unknown',
    industry,
    country,
    sectorAllocations,
    source: 'curated-classification',
  };
}

function singleSectorData(
  symbol: string,
  industry: string,
  sector: string,
  country = 'United States'
): SectorData {
  return {
    symbol,
    sector,
    industry,
    country,
    source: 'curated-classification',
  };
}

export function getCuratedSectorData(symbol: string): SectorData | null {
  return SYMBOL_TO_SECTOR_DATA[symbol.toUpperCase()] ?? null;
}
